import React from 'react'
import { Tag } from '@blueprintjs/core'
import { Tooltip2 } from '@blueprintjs/popover2'
import { NavigateFn } from '@reach/router'
import { IGridTableColumn, Skeleton, Icon, Avatar } from '@ui'
import { IBooking } from '@api'
import { DATE_PARSE_FORMAT } from '@utils/date'
import { navigateToAphrodite } from '@utils/navigateToAphrodite'
import track from '@track'
import Event from '@track/Event'
import { legacyMode } from 'App'
import { DateUtil } from '@utils/dateUtil'

type GetBookingColumns = (params: {
  navigate: NavigateFn | undefined
  features: string[]
  enableHealthScreening: boolean
}) => IGridTableColumn<IBooking>[]

const createBookingColumns: GetBookingColumns = ({ navigate, features, enableHealthScreening }) => [
  {
    label: 'Date & Time',
    sort: 'startDate',
    Cell: ({ skeleton, cell }) => {
      if (!cell || !cell.data) return null
      const timezone = cell?.data.space.floor.building?.timezone ?? ''
      const start = DateUtil.utc(cell?.data?.startDate, timezone)
      const end = DateUtil.utc(cell?.data?.endDate, timezone)
      return skeleton ? (
        <Skeleton withButton />
      ) : (
        <Tooltip2 content={DateUtil.tz(timezone).zoneAbbr()}>
          <div
            data-test="bookings_date"
            className="flex flex-row items-center"
            onClick={() => {
              navigate?.(`${window.location.pathname}?bookingId=${cell?.data.id}`)
              track.bookings({ eventName: Event.ROW_CLICK })
            }}
          >
            <div className="flex flex-col mr-3">
              <div className="font-medium">{start.format('MMM dd, yyyy')}</div>
              <div className="font-light">{start.format('hh:mma')}</div>
            </div>
            <Icon name="arrow-right" className="mr-3" />
            <div className="flex flex-col">
              <div className="font-medium">{end.format('MMM dd, yyyy')}</div>
              <div className="font-light">{end.format('hh:mma')}</div>
            </div>
          </div>
        </Tooltip2>
      )
    },
  },
  {
    label: 'Location',
    Cell: ({ skeleton, cell }) =>
      skeleton ? (
        <Skeleton />
      ) : (
        <div data-test="bookings_location" className="flex flex-col">
          <div
            className="font-medium hover:underline cursor-pointer"
            onClick={() => {
              const floorId = cell?.data.space.floor.id
              legacyMode
                ? navigateToAphrodite(`floor_map&floorId=${floorId}`)
                : navigate && navigate(`/floor_plan/${floorId}`)
            }}
          >
            {cell?.data.space.floor.name} - {cell?.data.space.floor.building?.name}
          </div>
          <div className="font-light">{cell?.data.space.floor.building?.city}</div>
        </div>
      ),
  },
  {
    label: 'Space',
    sort: 'space',
    Cell: ({ skeleton, cell }) =>
      skeleton ? (
        <Skeleton />
      ) : (
        <Tag round intent="primary">
          <span data-test="bookings_space">{cell?.data.space.externalId}</span>
        </Tag>
      ),
  },
  {
    label: 'Usage Type',
    sort: 'usageType',
    Cell: ({ skeleton, cell }) =>
      skeleton ? (
        <Skeleton />
      ) : (
        <Tag minimal round intent="none">
          {cell?.data.space.usageType.displayName}
        </Tag>
      ),
  },
  {
    label: 'Booked for',
    sort: 'bookedFor',
    Cell: ({ skeleton, cell }) =>
      skeleton ? (
        <Skeleton />
      ) : cell?.data ? (
        <div data-test="bookings_booked_for" className="flex items-center">
          <div className="flex items-center mr-3">
            <Avatar
              name={cell?.data.employee.name}
              assets={cell?.data.employee.assets}
              size={Avatar.Size.SM}
            />
            <div
              className="ml-2 pt-1 cursor-pointer"
              onClick={() => navigate?.(`?employeeId=${cell?.data.employee.id}`, {})}
            >
              <div className="font-bold hover:underline">{cell?.data.employee.name}</div>
              <div className="font-light">{cell?.data.employee.title}</div>
            </div>
          </div>
        </div>
      ) : null,
  },
  ...(features.includes('healthCheckAcknowledgment') && enableHealthScreening
    ? [
        {
          label: 'Health Check',
          sort: 'healthCheck',
          Cell: ({ skeleton, cell }) =>
            skeleton ? (
              <Skeleton />
            ) : cell?.data ? (
              <div className="flex items-center">
                <div data-test="bookings_health" className="flex items-center mr-3">
                  {cell?.data.passedHealthCheck === true
                    ? 'yes'
                    : cell?.data.passedHealthCheck === false
                    ? 'no'
                    : ''}
                </div>
              </div>
            ) : null,
        } as IGridTableColumn<IBooking>,
      ]
    : []),
  {
    label: 'Booked by',
    sort: 'bookedBy',
    Cell: ({ skeleton, cell }) =>
      skeleton ? (
        <Skeleton />
      ) : cell?.data.bookedBy ? (
        <div className="flex items-center">
          <div data-test="bookings_booked_by" className="flex items-center mr-3">
            <Avatar
              name={cell?.data.bookedBy?.employee.name}
              assets={cell?.data.bookedBy?.employee.assets}
              size={Avatar.Size.SM}
            />
            <div
              className="ml-2 pt-1 cursor-pointer"
              onClick={() => navigate?.(`?employeeId=${cell?.data.bookedBy?.employee.id}`)}
            >
              <div className="font-bold hover:underline">{cell?.data.bookedBy?.employee.name}</div>
              <div className="font-light">{cell?.data.bookedBy?.employee.title}</div>
            </div>
          </div>
        </div>
      ) : (
        <div data-test="bookings_booked_by">Unknown</div>
      ),
  },
]

export default createBookingColumns
